/* eslint-disable no-plusplus */
import React, { useEffect, useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import BackpackIcon from "@mui/icons-material/Backpack";
import FilterListIcon from "@mui/icons-material/FilterList";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SchoolIcon from "@mui/icons-material/School";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import Barchart from "components/smea/Charts/Barchart";
import DivisionMPSModal from "modals/smea/displayCards/admin/DivisionMPSModal";
import CRPerGradeLevelModal from "modals/smea/displayCards/admin/CRPerGradeLevelModal";
import SchoolsCompletionRateModal from "modals/smea/displayCards/admin/SchoolsCompletionRateModal";
import { isNaN } from "formik";
import CompletionRatePerSubjectModal from "modals/smea/displayCards/admin/CompletionRatePerSubjectModal";
import { useStateContext } from "contexts/ContextProvider";
import useAxiosPrivate from "contexts/interceptors/axios";
import FilterModal from "modals/smea/miscellaneous/FilterModal";
import DashboardDataTiles from "components/smea/DashboardDataTiles";
import "./admin-dashboard.css";

function AdminDashboard() {
  const {
    auth,
    schoolsCR,
    setSchoolsCR,
    schoolsCRCount,
    setSchoolsCRCount,
    subjectData,
    setSubjectData,
    subjectCRCount,
    setSubjectCRCount,
    gradeLevelData,
    setGradeLevelData,
    gradeLevelCRCount,
    setGradeLevelCRCount,

    schoolYear,
    setSchoolYear,
    quarter,
    setQuarter,
  } = useStateContext();
  const axiosPrivate = useAxiosPrivate();

  const [results, setResults] = useState([]);
  const [consolidatedData, setConsolidatedData] = useState([]);
  const [showLabel, setShowLabel] = useState(true);
  const [CRPerGradeLevelOpen, setCRPerGradeLevelOpen] = useState(false);
  const [CRPerSubjectOpen, setCRPerSubjectOpen] = useState(false);
  const [divisionMPSOpen, setDivisionMPSOpen] = useState(false);
  const [schoolsCROpen, setSchoolsCROpen] = useState(false);

  const [openFilter, setOpenFilter] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [CRLoading, setCRLoading] = useState(false);

  const handleGetAllResults = () => {
    if (quarter && schoolYear) {
      axiosPrivate
        .get(`/competency/getAllResults`, { params: { quarter, schoolYear } })
        .then((res) => {
          setResults(res.data);
        })
        .catch((err) => setError(err.message));
    } else {
      setResults([]);
    }
  };

  const handleGetAllConsolidatedData = () => {
    if (quarter) {
      axiosPrivate
        .get(`/competency/allSchoolsConsolidatedData`, {
          params: {
            quarter,
            schoolYear,
          },
        })
        .then((res) => {
          setConsolidatedData(res?.data);
        })
        .catch((err) => setError(err.message));
    } else {
      setConsolidatedData([]);
    }
  };

  const overall = () => {
    let overallMps = 0;

    // eslint-disable-next-line array-callback-return
    results?.map((res) => {
      overallMps += res.overallAverageMPS;
    });

    const avgOverallMPS = overallMps / results.length;

    return isNaN(avgOverallMPS) ? 0 : `${parseFloat(avgOverallMPS).toFixed(2)}`;
  };

  useEffect(() => {
    setLoading(true);
    setError("");

    Promise.all([handleGetAllResults(), handleGetAllConsolidatedData()])
      .then(() => setLoading(false))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [quarter]);

  const handleGetAllSchoolCR = () => {
    if (CRLoading === false) {
      setCRLoading(true);
      setError("");

      axiosPrivate
        .get(`/competency/allSchoolsCompletionRate`, {
          params: {
            quarter,
            schoolYear,
          },
        })
        .then((e) => {
          const data = e?.data;
          setSchoolsCRCount(data.schoolsCRCount);
          setSchoolsCR(data.allSchoolsCR);
        })
        .catch((err) => setError(err.message))
        .finally(() => {
          setCRLoading(false);
        });
    }
  };

  const handleGetAllCRPerSubject = () => {
    if (CRLoading === false) {
      setCRLoading(true);
      setError("");

      axiosPrivate
        .get(`/competency/allSchoolsCRPerSubject`, {
          params: {
            quarter,
            schoolYear,
          },
        })
        .then((e) => {
          const data = e?.data;

          setSubjectCRCount(data.subjectsCompletedCount);
          setSubjectData(data.subjectsCR);
        })
        .catch((err) => setError(err.message))
        .finally(() => {
          setCRLoading(false);
        });
    }
  };

  const handleGetAllCRPerGL = () => {
    if (CRLoading === false) {
      setCRLoading(true);
      setError("");

      axiosPrivate
        .get(`/competency/allSchoolsCRPerGL`, {
          params: {
            quarter,
            schoolYear,
          },
        })
        .then((e) => {
          const data = e?.data;
          setGradeLevelCRCount(data.GLCompletedCount);
          setGradeLevelData(data.gradeLevelCR);
        })
        .catch((err) => setError(err.message))
        .finally(() => {
          setCRLoading(false);
        });
    }
  };

  const contents = [
    {
      title: "Schools",
      onHoverTitle: `${
        schoolsCR.length === 0
          ? "Schools Completion Rate Not Loaded"
          : "View Schools Completion Rate Table"
      }`,
      onClickFunction: () => {
        if (schoolsCR.length !== 0 && !CRLoading && schoolYear) {
          setSchoolsCROpen(true);
        }
      },
      value:
        // eslint-disable-next-line no-nested-ternary
        loading || CRLoading ? (
          <CircularProgress />
        ) : schoolsCR.length !== 0 ? (
          `${schoolsCRCount || 0}/35`
        ) : (
          <Typography sx={{ fontSize: "27px" }}>Uncalculated</Typography>
        ),
      tileColor: "linear-gradient(120deg, #cf1515, #9319a6)",
      icon: <SchoolIcon sx={{ color: "lightgray", fontSize: "26px" }} />,
    },
    {
      title: "Subjects",
      onHoverTitle: `${
        subjectData.length === 0
          ? "Completion Rate Per Subject Not Loaded"
          : "View Schools Completion Rate Per Subject Table"
      }`,
      onClickFunction: () => {
        if (subjectData.length !== 0 && !CRLoading) {
          setCRPerSubjectOpen(true);
        }
      },
      value:
        // eslint-disable-next-line no-nested-ternary
        loading || CRLoading ? (
          <CircularProgress />
        ) : subjectData.length !== 0 ? (
          `${subjectCRCount || 0}/${subjectData.length}`
        ) : (
          <Typography sx={{ fontSize: "27px" }}>Uncalculated</Typography>
        ),
      tileColor: "linear-gradient(120deg, #246fc9, #184c8c)",
      icon: <ListAltIcon sx={{ color: "lightgray", fontSize: "26px" }} />,
    },
    {
      title: "Grade Levels",
      onHoverTitle: `${
        gradeLevelData.length === 0
          ? "Completion Rate Per Grade Level Not Loaded"
          : "View Schools Completion Rate Per Grade Level Table"
      }`,
      onClickFunction: () => {
        if (gradeLevelData.length !== 0 && !CRLoading) {
          setCRPerGradeLevelOpen(true);
        }
      },
      value:
        // eslint-disable-next-line no-nested-ternary
        loading || CRLoading ? (
          <CircularProgress />
        ) : gradeLevelData.length !== 0 ? (
          `${gradeLevelCRCount || 0}/10`
        ) : (
          <Typography sx={{ fontSize: "27px" }}>Uncalculated</Typography>
        ),
      tileColor: "linear-gradient(120deg, #f09116, #a8650d)",
      icon: <BackpackIcon sx={{ color: "lightgray", fontSize: "26px" }} />,
    },
    {
      title: "Division MPS",
      onClickFunction: () => {
        if (!CRLoading) {
          setDivisionMPSOpen(true);
        }
      },
      value: loading || CRLoading ? <CircularProgress /> : overall(),
      tileColor: "linear-gradient(120deg, #27bf13, #207a14)",
      icon: <PersonAddIcon sx={{ color: "lightgray", fontSize: "26px" }} />,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#f0f0f0",
        px: 7,
        py: 2,
      }}
    >
      <FilterModal
        open={openFilter}
        handleClose={() => setOpenFilter(false)}
        schoolYear={schoolYear}
        setSchoolYear={setSchoolYear}
        quarter={quarter}
        setQuarter={setQuarter}
        handleFilter={() => setOpenFilter(false)}
        error={error}
      />
      <CRPerGradeLevelModal
        open={CRPerGradeLevelOpen}
        handleClose={() => setCRPerGradeLevelOpen(false)}
        loading={loading}
        error={error}
        gradeLevelData={gradeLevelData}
      />
      <CompletionRatePerSubjectModal
        open={CRPerSubjectOpen}
        handleClose={() => setCRPerSubjectOpen(false)}
        loading={loading}
        error={error}
        subjectData={subjectData}
      />
      <DivisionMPSModal
        open={divisionMPSOpen}
        handleClose={() => setDivisionMPSOpen(false)}
        consolidatedData={consolidatedData}
        loading={loading}
        error={error}
      />
      <SchoolsCompletionRateModal
        open={schoolsCROpen}
        handleClose={() => setSchoolsCROpen(false)}
        data={schoolsCR}
        loadingState={loading}
        quarter={quarter}
        schoolYear={schoolYear}
      />
      {error}
      {loading}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          width: "100%",
          my: 2,
        }}
      >
        <Box>
          <Typography
            sx={{ textAlign: "left", fontSize: "30px", fontWeight: "bold" }}
          >
            {auth?.role === "admin"
              ? "Welcome to the Admin Dashboard!"
              : "Welcome to the DepEd Official's Dashboard!"}
          </Typography>
          <Typography
            sx={{ textAlign: "left", fontSize: "20px", fontWeight: "bold" }}
          >
            {auth?.role === "admin" ? "Admin" : auth?.username}
          </Typography>
        </Box>
        <Button
          onClick={() => setOpenFilter(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "15px",
            // backgroundColor: "#383838",
            // color: "#fff",
            borderRadius: "10px",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "#2e3887",
              color: "#fff",
            },
          }}
        >
          <FilterListIcon sx={{ mr: 1 }} />
          Filter
        </Button>
      </Box>

      {contents && contents.length > 0 && (
        <Box sx={{ width: "100%", my: "2vw" }}>
          <DashboardDataTiles contents={contents} loadingState={loading} />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          width: "100%",
          gap: "4vw",
        }}
      >
        <Box>
          <Tooltip
            title={!quarter && "Quarter not specified"}
            placement="bottom"
            backgroundColor="red"
          >
            <button
              id="btn-calculate"
              className="btn-calculate"
              type="submit"
              onClick={handleGetAllSchoolCR}
              disabled={CRLoading || !quarter || !schoolYear}
              style={{
                cursor:
                  CRLoading || !quarter || !schoolYear ? "default" : "pointer",
                color:
                  CRLoading || !quarter || !schoolYear ? "#364d60" : "#fff",
              }}
            >
              <span>All Schools Completion Rate</span>
              <span>Calculate</span>
            </button>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip
            title={!quarter && "Quarter not specified"}
            placement="bottom"
            backgroundColor="red"
          >
            <button
              id="btn-calculate"
              className="btn-calculate"
              type="submit"
              onClick={handleGetAllCRPerSubject}
              disabled={CRLoading || !quarter || !schoolYear}
              style={{
                cursor:
                  CRLoading || !quarter || !schoolYear ? "default" : "pointer",
                color:
                  CRLoading || !quarter || !schoolYear ? "#364d60" : "#fff",
              }}
            >
              <span>All Schools Completion Rate Per Subject</span>
              <span>Calculate</span>
            </button>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip
            title={!quarter && "Quarter not specified"}
            placement="bottom"
            backgroundColor="red"
          >
            <button
              id="btn-calculate"
              className="btn-calculate"
              type="submit"
              onClick={handleGetAllCRPerGL}
              disabled={CRLoading || !quarter || !schoolYear}
              style={{
                cursor:
                  CRLoading || !quarter || !schoolYear ? "default" : "pointer",
                color:
                  CRLoading || !quarter || !schoolYear ? "#364d60" : "#fff",
              }}
            >
              <span>All Schools Completion Rate Per Grade Level</span>
              <span>Calculate</span>
            </button>
          </Tooltip>
        </Box>
      </Box>

      {CRLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            my: 2,
          }}
        >
          <Box>
            <Typography sx={{ mr: 2 }}>
              Computing All Schools Completion Rate...
            </Typography>
            <Typography sx={{ mr: 2 }}>This may take a while</Typography>
          </Box>
          <CircularProgress />
        </Box>
      ) : (
        schoolsCR.length !== 0 && (
          <Box
            sx={{
              mt: 4,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  textAlign: "left",
                  fontSize: "25px",
                  fontWeight: "bold",
                }}
              >
                Schools Completion Rate Graph
              </Typography>
              <Button onClick={() => setShowLabel(!showLabel)}>
                {showLabel ? "Hide Label" : "Show Label"}
              </Button>
            </Box>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                overflowX: "none",
                mb: 8,
                scrollbarWidth: "0.3rem",
                scrollbarHeight: "0.3rem",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                  height: "0.3rem",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "gray",
                },
                "@media (max-width: 1400px)": {
                  overflowX: "auto",
                },
              }}
            >
              <Barchart
                data={schoolsCR}
                showLabel={showLabel}
                loadingState={loading}
              />
            </Box>
          </Box>
        )
      )}
    </Box>
  );
}

export default AdminDashboard;
